<template>
    <ul class="navbar-item theme-brand flex-row branding-chrome">
        <li class="nav-item theme-logo">
            <router-link :to="homeTo" class="branding-chrome__logo-link">
                <img
                    :src="logoSrc"
                    class="navbar-logo branding-chrome__logo"
                    :alt="title"
                    @error="onLogoError"
                />
            </router-link>
            <slot name="mobile-extra" />
        </li>
        <li class="nav-item theme-text branding-chrome__text-wrap">
            <router-link :to="homeTo" class="nav-link branding-chrome__title">
                {{ title }}
            </router-link>
            <span
                v-if="showPlatformName && platformName"
                class="branding-chrome__platform"
            >
                {{ platformName }}
            </span>
        </li>
    </ul>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useBrandingStore, PLATFORM_LOGO_SRC } from '@/stores/branding';

const props = defineProps({
    homeTo: {
        type: [String, Object],
        default: '/',
    },
    override: {
        type: Object,
        default: null,
    },
});

const branding = useBrandingStore();
const logoFailed = ref(false);

const title = computed(() => {
    if (props.override?.display_name) return props.override.display_name;
    return branding.chromeTitle;
});

const logoSrc = computed(() => {
    if (logoFailed.value) return PLATFORM_LOGO_SRC;
    if (props.override?.logo_url) return props.override.logo_url;
    return branding.chromeLogo;
});

/** Mostra o nome da plataforma abaixo da marca do professor (sem “powered by”). */
const showPlatformName = computed(() => {
    if (props.override) {
        return Boolean(props.override.is_customized || props.override.logo_url || props.override.display_name);
    }
    return branding.isCustomized;
});

const platformName = computed(() => {
    if (typeof props.override?.platform_name === 'string' && props.override.platform_name.trim()) {
        return props.override.platform_name.trim();
    }
    return branding.platformName;
});

watch(
    () => [props.override?.logo_url, branding.logoUrl],
    () => {
        logoFailed.value = false;
    }
);

function onLogoError() {
    // Evita loop se o fallback da plataforma também falhar.
    if (logoSrc.value === PLATFORM_LOGO_SRC) return;
    logoFailed.value = true;
}
</script>

<style scoped>
.branding-chrome {
    justify-content: flex-start !important;
    align-items: center;
    text-align: left;
    gap: 0.5rem;
}

.branding-chrome__logo-link {
    display: flex;
    align-items: center;
}

.branding-chrome__text-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    line-height: 1.15;
    text-align: left;
    min-width: 0;
}

.branding-chrome__title {
    /* No header pintado com a cor principal, usa contraste; senão primary/inherit */
    color: var(--brand-on-header, var(--brand-primary, inherit));
    display: block;
    width: 100%;
    line-height: 1.2 !important;
    padding: 0 !important;
    margin: 0;
    text-align: left !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 220px;
}

.branding-chrome__platform {
    display: block;
    width: 100%;
    font-size: 10px;
    font-weight: 500;
    color: var(--brand-on-header-muted, inherit);
    opacity: 0.72;
    margin: 0;
    padding: 0;
    white-space: nowrap;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left !important;
    line-height: 1.2;
}

.branding-chrome__logo {
    max-height: 36px;
    width: auto;
    object-fit: contain;
    border-radius: 4px;
}

@media (max-width: 991.98px) {
    .branding-chrome__title,
    .branding-chrome__platform {
        max-width: 140px;
    }
}
</style>
